import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { colors } from '../theme/colors.stylex';
import { controlMetrics } from '../theme/controls.stylex';
import type { BadgeProps } from './Badge.types';

const styles = stylex.create({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '3px',
    paddingLeft: '5px',
    paddingRight: '5px',
    paddingTop: '2px',
    paddingBottom: '2px',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    fontStyle: 'italic',
  },
  joined: {
    display: 'inline-flex',
    alignItems: 'stretch',
    overflow: 'hidden',
    borderRadius: '0.25rem',
    borderWidth: controlMetrics.borderWidth,
    borderStyle: 'solid',
  },
  label: { fontWeight: 700, fontStyle: 'normal' },
  body: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
});
// Preview and success share colors today but remain separate variants so either can evolve independently.
const variants = stylex.create({
  new: { backgroundColor: colors.badgeNewBackground, color: colors.badgeNewText },
  preview: { backgroundColor: colors.badgePositiveBackground, color: colors.badgePositiveText },
  success: { backgroundColor: colors.badgePositiveBackground, color: colors.badgePositiveText },
  neutral: {
    backgroundColor: colors.badgeNeutralBackground,
    color: colors.mutedText,
    borderWidth: controlMetrics.borderWidth,
    borderStyle: 'solid',
    borderColor: 'transparent',
  },
  error: { backgroundColor: colors.badgeErrorBackground, color: colors.borderInvalid },
});
const labels = stylex.create({
  new: { backgroundColor: colors.warning, color: colors.black },
  preview: { backgroundColor: colors.badgePositiveText, color: colors.badgePositiveLabelText },
  success: { backgroundColor: colors.badgePositiveText, color: colors.badgePositiveLabelText },
  neutral: { backgroundColor: colors.mutedText, color: colors.white },
  error: { backgroundColor: colors.borderInvalid, color: colors.white },
});
const borders = stylex.create({
  new: { borderColor: colors.badgeNewBorder },
  preview: { borderColor: colors.badgePositiveBorder },
  success: { borderColor: colors.badgePositiveBorder },
  neutral: { borderColor: colors.badgeNeutralBorder },
  error: { borderColor: colors.badgeErrorBorder },
});

/** A compact status badge, optionally joined to a solid leading label. */
export const Badge = ({ children, variant = 'new', label, testId, extraClassNames = '', xstyle }: BadgeProps) => {
  const hasLabel = label !== undefined && label !== null;
  const bodyProps = stylex.props(styles.base, variants[variant], hasLabel && styles.body, xstyle);
  if (!hasLabel) {
    return (
      <span
        {...bodyProps}
        data-testid={testId}
        data-variant={variant}
        className={clsx(bodyProps.className, extraClassNames)}>
        {children}
      </span>
    );
  }
  return (
    <span data-testid={testId} data-variant={variant} {...stylex.props(styles.joined, borders[variant])}>
      <span {...stylex.props(styles.base, styles.label, labels[variant])}>{label}</span>
      <span {...bodyProps} className={clsx(bodyProps.className, extraClassNames)}>
        {children}
      </span>
    </span>
  );
};
export default Badge;
